/**
 * "Taken in je agenda" — zet je ingeplande taken als blokken in je eigen Outlook-agenda.
 *
 * Pilot Pepijn sinds 15 sep 2026. Sinds stap 2 (27 okt) zet de beheerder je aan (Admin → Taken in agenda);
 * zelf kun je het uitzetten, en weer aanzetten als de beheerder je had aangezet. Met de dagplanning krijgt
 * een taak één blok per dag, over meetings heen en nooit na het einde van je werkdag. Verplaats je een blok
 * zelf, dan blijft het staan; is de taak klaar, dan verdwijnt het.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';

import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';

const URL = `${BACKEND_URL}/Api/agenda-blokken`;

const tijdLabel = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} ${d.toTimeString().slice(0, 5)}`;
};

export default function AgendaBlokkenPanel() {
  const [voorkeur, setVoorkeur] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [voorbeeld, setVoorbeeld] = useState(null);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${URL}/voorkeur`);
      setVoorkeur(await leesJson(r, 'Voorkeur laden'));
    } catch (e) { meldFout(e, 'Voorkeur laden'); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const bewaar = async (velden) => {
    setBezig(true);
    try {
      const r = await authFetch(`${URL}/voorkeur`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden) });
      const v = await leesJson(r, 'Voorkeur opslaan');
      setVoorkeur(v);
      toast.success(v.aan ? 'Aan — je taken verschijnen binnen 10 minuten in je agenda' : 'Uit — bestaande blokken worden bij de volgende ronde opgeruimd');
    } catch (e) { meldFout(e, 'Voorkeur opslaan'); }
    finally { setBezig(false); }
  };

  const toonVoorbeeld = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${URL}/voorbeeld`, { method: 'POST' });
      setVoorbeeld(await leesJson(r, 'Voorbeeld ophalen'));
    } catch (e) { meldFout(e, 'Voorbeeld ophalen'); }
    finally { setBezig(false); }
  };

  const nuSyncen = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${URL}/nu`, { method: 'POST' });
      const d = await leesJson(r, 'Nu synchroniseren');
      if (d.gepauzeerd) { toast('Taken in je agenda staat even stil voor onderhoud. Probeer het later opnieuw.'); return; }
      if (d.bezig) { toast('Je agenda wordt al bijgewerkt. Probeer het over een minuut opnieuw.'); return; }
      const later = d.uitgesteld ? `, ${d.uitgesteld} volgt bij de volgende ronde` : '';
      toast.success(`Klaar: ${d.nieuw?.length || 0} nieuw, ${d.bijgewerkt?.length || 0} bijgewerkt, ${d.verwijderd?.length || 0} weg${later}`);
      setVoorbeeld(d);
    } catch (e) { meldFout(e, 'Nu synchroniseren'); }
    finally { setBezig(false); }
  };

  if (!voorkeur) return <CircularProgress size={20} sx={{ m: 2 }} />;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <FormControlLabel
        control={<Switch checked={Boolean(voorkeur.aan)} disabled={bezig || (!voorkeur.aan && !voorkeur.magAanzetten)}
          onChange={(e) => bewaar({ aan: e.target.checked })} />}
        label={<Typography sx={{ fontWeight: 700 }}>Zet mijn ingeplande taken als blokken in mijn Outlook-agenda</Typography>}
      />
      {!voorkeur.aan && !voorkeur.magAanzetten && (
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
          Dit zet je beheerder voor je aan.
        </Typography>
      )}
      <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, mb: 2 }}>
        {voorkeur.stap2
          ? 'Elke taak met uren krijgt een blok per dag waarop hij loopt, op volgorde van prioriteit. Een blok mag over een meeting heen en wordt dan zoveel langer; past het niet meer in je werkdag, dan staat dat in het blok. Vrije dagen en draaidagen blijven leeg.'
          : 'Elke taak met uren wordt één blok, geplaatst in het eerste gat tussen je afspraken.'}
        {' '}Verschuif je een blok zelf, dan laat intern het staan. Afgeronde taken verdwijnen weer. Blokken heten
        "🧩 projectnummer · klant · taak" en tellen niet dubbel mee in de capaciteit.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField label="Werkdag van" type="time" size="small" value={voorkeur.werkdagStart || '09:00'} disabled={bezig}
          onChange={(e) => setVoorkeur({ ...voorkeur, werkdagStart: e.target.value })}
          onBlur={(e) => bewaar({ werkdagStart: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
        <TextField label="tot" type="time" size="small" value={voorkeur.werkdagEind || '17:30'} disabled={bezig}
          onChange={(e) => setVoorkeur({ ...voorkeur, werkdagEind: e.target.value })}
          onBlur={(e) => bewaar({ werkdagEind: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
        <TextField label="Vooruit (werkdagen)" type="number" size="small" value={voorkeur.horizonWerkdagen || 10} disabled={bezig}
          inputProps={{ min: 1, max: 30 }}
          onChange={(e) => setVoorkeur({ ...voorkeur, horizonWerkdagen: Number(e.target.value) })}
          onBlur={(e) => bewaar({ horizonWerkdagen: Number(e.target.value) })} sx={{ width: 170 }} />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" size="small" onClick={toonVoorbeeld} disabled={bezig} sx={{ textTransform: 'none' }}>
          Laat zien wat er zou komen
        </Button>
        {voorkeur.aan && (
          <Button variant="contained" size="small" disableElevation onClick={nuSyncen} disabled={bezig} sx={{ textTransform: 'none' }}>
            Nu bijwerken
          </Button>
        )}
      </Stack>

      {voorbeeld && (
        <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#f8fafc' }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Periode {voorbeeld.horizon?.[0]} t/m {voorbeeld.horizon?.[1]} · {voorbeeld.taken} taken met uren
            {voorbeeld.dry_run ? ' · voorbeeld, niets geschreven' : ''}
          </Typography>
          {(voorbeeld.dagen || []).filter((d) => (d.blokken || []).length || d.tekort).map((d) => (
            <Box key={d.dag} sx={{ display: 'flex', gap: 1.5, py: 0.3 }}>
              <Typography variant="caption" sx={{ width: 90, flexShrink: 0, color: '#64748b' }}>
                {new Date(`${d.dag}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
              </Typography>
              <Box>
                {(d.blokken || []).map((b, i) => (
                  <Typography key={i} variant="body2">{b.start.slice(11, 16)}–{b.eind.slice(11, 16)} {b.project ? `${b.project} · ` : ''}{b.taak}</Typography>
                ))}
                {d.tekort > 0 && <Typography variant="caption" sx={{ color: '#b45309' }}>past niet: {Math.round(d.tekort / 6) / 10} u</Typography>}
              </Box>
            </Box>
          ))}
          {!voorbeeld.dagen && (voorbeeld.nieuw || []).length === 0 && (voorbeeld.bijgewerkt || []).length === 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>Niets nieuws te plaatsen{voorbeeld.ongewijzigd ? ` (${voorbeeld.ongewijzigd} blokken staan al goed)` : ''}.</Typography>
          )}
          {!voorbeeld.dagen && (voorbeeld.nieuw || []).map((b, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.4 }}>
              <Chip size="small" label={tijdLabel(b.start)} sx={{ height: 20, fontSize: '0.68rem' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.project ? `${b.project} · ` : ''}{b.taak}</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>{b.uren}u</Typography>
              {b.past_niet && <Chip size="small" color="warning" label="past niet tussen afspraken" sx={{ height: 20, fontSize: '0.65rem' }} />}
            </Box>
          ))}
          {(voorbeeld.zonder_uren || []).length > 0 && (
            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
              Zonder uren (geen blok): {voorbeeld.zonder_uren.join(', ')}
            </Alert>
          )}
          {(voorbeeld.fouten || []).length > 0 && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{voorbeeld.fouten.join(' · ')}</Alert>}
        </Box>
      )}
    </Box>
  );
}
